import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Container, Form, Modal, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { BiPlus, BiTrash } from "react-icons/bi";
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { axiosAuth_IDENT_MGMT_API, axiosAuth_SECURITY_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';
import { testJSON } from '../utils/jsUtils';


const DatakeyAdminDetailsView = (props) => {

  // Utilities
  const { i18n, t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  const [objAttrs,        setObjAttrs]        = useState({});
  const [currentObjAttrs, setCurrentObjAttrs] = useState({});

  // Key permission stuff
  const [permList,              setPermList]              = useState([]);

  const [permAttrs,             setPermAttrs]             = useState({});
  const [currentPermAttrs,      setCurrentPermAttrs]      = useState({});

  const [showAddPermsDialog,    setShowAddPermsDialog]    = useState(false);
  const [addPermErrMsg,         setAddPermsErrMsg]        = useState('');

  const [showPermDetailsDialog, setShowPermDetailsDialog] = useState(false);
  const [permDetailsErrMsg,     setPermDetailsErrMsg]     = useState('');

  const [showUserSelector,      setShowUserSelector]      = useState(false);
  const [userSelectorErrMsg,    setUserSelectorErrMsg]    = useState('');

  // Permission x users
  const [permUserList,          setPermUserList]          = useState([]);
  const [ungrantedUserList,     setUngrantedUserList]     = useState([]);


  // Initialize state
  useEffect(() => {

    if (props && props.objDetails && props.objDetails.id) {
      fetchObj(props.objDetails.id);
      fetchKeyPerms(props.objDetails.id);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const fetchObj = (id) => {

    return new Promise(async (resolve, reject) => {
      try {
        // Use searching instead of id, is because using id will get a decrypted key and IV.  For admin, we want the
        // un-decrypted key and IV.
        const res = await axiosAuth_SECURITY_API.get("data-keys?searchTerms=" + id);

        resolve(res.data);

        if (res.status === 200) {

          let tempDetails = JSON.parse (JSON.stringify (res.data.data[0]));
          tempDetails.metadata = JSON.stringify (res.data.data[0].metadata, undefined, 4);

          setObjAttrs(tempDetails);
          setCurrentObjAttrs(tempDetails);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const fetchKeyPerms = (keyId) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_SECURITY_API.get("key-permissions/" + keyId);

        resolve(res.data);

        if (res.status === 200) {
          let tmpPermList =  JSON.parse (JSON.stringify (res.data));

          for (let i=0; i<tmpPermList.length; i++) {
            tmpPermList[i].permSetting = JSON.stringify (res.data[i].permSetting, undefined, 4);
          }

          setPermList(tmpPermList);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const fetchKeyPermUsers = (permissionId) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_SECURITY_API.get("key-permissions/" + permissionId + "/users");

        resolve(res.data);

        if (res.status === 200) {
          setPermUserList (res.data);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }


  const fetchUngrantedUsers = () => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_IDENT_MGMT_API.get("users");

        resolve(res.data);

        if (res.status === 200) {

          let tmpList = [];

          for (let i=0; i<res.data.length; i++) {
            let id = permUserList.find( ({ userId }) => userId === res.data[i].id);

            if (!id)
              tmpList.push(res.data[i]);
          }

          setUngrantedUserList(tmpList);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }



  // View vs Edit Modes of Data Key
  const [editMode, setEditMode] = useState(false);

  const [errMsg, setErrMsg] = useState('');

  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }

  const onAttrsInputChange = (e) => {
    setObjAttrs({ ...objAttrs, [e.target.name]: e.target.value })
  }

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setObjAttrs(currentObjAttrs);

    setEditMode(false);
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    // Validate fields
    if (!objAttrs.description || objAttrs.description.length === 0) {
      setErrMsg(t("I18N_ERR_Need_description"));
      return;
    } else if (   objAttrs.metadata
               && (objAttrs.metadata !== '')
               && (!testJSON(objAttrs.metadata.trim()))) {
      setErrMsg(t("I18N_ERR_Metadata_need_valid_JSON"));
      return;
    }

    let reqBody = JSON.parse (JSON.stringify (objAttrs));

    if ((objAttrs.metadata == null) || (objAttrs.metadata === '')) {
      reqBody.metadata = null;
    } else {
      reqBody.metadata = JSON.parse(objAttrs.metadata.trim());
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_SECURITY_API.put("data-keys/" + objAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          // Restore UI to viewing mode
          setEditMode(false);

          fetchObj(objAttrs.id);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }

  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  // ----- Permission Details Dialog Stuff -----

  const [editModePerm, setEditModePerm] = useState(false);

  function handlePermDetails(permission) {
    setPermAttrs(permission);
    setCurrentPermAttrs(permission);

    fetchKeyPermUsers(permission.id);
    fetchUngrantedUsers();

    setShowPermDetailsDialog(true);
  }

  function handlePermDetailsDialogClose() {
    setPermAttrs({});
    setShowPermDetailsDialog(false);
  }

  const handleEditAttrsPerm = (e) => {
    e.preventDefault();
    setEditModePerm(true);
  }

  const handleEditPermsCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setPermAttrs(currentPermAttrs);

    setEditModePerm(false);
  }

  const handleEditPermsSubmit = (e) => {
    e.preventDefault();

    // Validate form inputs
    if (permAttrs.name.length === 0) {
      setAddPermsErrMsg(t("I18N_ERR_Need_name"));
    } else if (permAttrs.name.length > 50) {
      setAddPermsErrMsg(t("I18N_ERR_Max_len_name") + 50);
      return;
    } else if (!permAttrs.description || permAttrs.description.trim().length === 0) {
      setAddPermsErrMsg(t("I18N_ERR_Need_description"));
      return;
    } else if (permAttrs.description.length > 250) {
      setAddPermsErrMsg(t("I18N_ERR_Max_len_desc") + 250);
      return;
    } else if (   permAttrs.permSetting
               && (permAttrs.permSetting !== '')
               && (!testJSON(permAttrs.permSetting.trim()))) {
      // Validate configuration to be valid JSON
      setAddPermsErrMsg(t("I18N_ERR_Config_need_valid_JSON"));
      return;
    }

    let reqBody = JSON.parse (JSON.stringify (permAttrs));

    reqBody.keyId = objAttrs.id;

    if ((permAttrs.permSetting == null) || (permAttrs.permSetting === '')) {
      reqBody.permSetting = null;
    } else {
      reqBody.permSetting = JSON.parse(permAttrs.permSetting.trim());
    }

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_SECURITY_API.put("key-permissions/" + permAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          // Restore UI to viewing mode
          setEditModePerm(false);
          //handlePermDetailsDialogClose();

          fetchKeyPerms(objAttrs.id);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setPermDetailsErrMsg(t(translateAPIError(error)));
        }
      }
    });
  }

  // ----- Perm add stuff -----

  const onAttrsInputChangePerm = (e) => {
    setPermAttrs({ ...permAttrs, [e.target.name]: e.target.value })
  }

  const handleAddPerms = (e) => {
    setShowAddPermsDialog(true);
  }

  function handleAddPermsClose() {
    setPermAttrs({});
    setShowAddPermsDialog(false);
  }

  function handleAddPermsSubmit() {

    // Validate form inputs
    if (permAttrs.name.length === 0) {
      setAddPermsErrMsg(t("I18N_ERR_Need_name"));
    } else if (permAttrs.name.length > 50) {
      setAddPermsErrMsg(t("I18N_ERR_Max_len_name") + 50);
      return;
    } else if (!permAttrs.description || permAttrs.description.trim().length === 0) {
      setAddPermsErrMsg(t("I18N_ERR_Need_description"));
      return;
    } else if (permAttrs.description.length > 250) {
      setAddPermsErrMsg(t("I18N_ERR_Max_len_desc") + 250);
      return;
    } else if (   permAttrs.permSetting
               && (permAttrs.permSetting !== '')
               && (!testJSON(permAttrs.permSetting.trim()))) {
      // Validate configuration to be valid JSON
      setAddPermsErrMsg(t("I18N_ERR_Config_need_valid_JSON"));
      return;
    }


    let reqBody = JSON.parse (JSON.stringify (permAttrs));

    reqBody.keyId = objAttrs.id;

    if ((permAttrs.permSetting == null) || (permAttrs.permSetting === '')) {
      reqBody.permSetting = null;
    } else {
      reqBody.permSetting = JSON.parse(permAttrs.permSetting.trim());
    }

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_SECURITY_API.post("key-permissions", reqBody);

        resolve(res.data);

        if (res.status === 201) {
          //setAddPermsErrMsg('');
          setShowAddPermsDialog(false);
          setPermAttrs({});
          fetchKeyPerms(objAttrs.id);
        } else {
          // ##### NEED TO DECIDE WHAT TO DO #####
        }
      } catch (error) {
        setAddPermsErrMsg(t(translateAPIError(error)));
        return;
      }
    });
  }


  function renderPerm(perm, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td style={{width:"25%"}}>
          <span style={{cursor:'pointer', color:'SteelBlue'}}
                onClick={() => handlePermDetails(perm)}>
            {perm.name}
          </span>
        </td>
        <td style={{width:"30%"}}>{perm.description}</td>
        <td style={{width:"40%"}}>{perm.permSetting}</td>
        <td style={{width:"5%"}}>
          <OverlayTrigger overlay={<Tooltip id="tooltip-remove-screen" style={{fontSize:'11px'}}>{t("I18N_TIP_Delete_perm")}</Tooltip>}>
            <span style={{cursor:'pointer', color:'SteelBlue'}}
                  onClick={() => handleObjDelete(perm)}><BiTrash size={16} />
            </span>
          </OverlayTrigger>
        </td>
      </tr>
    )
  }


  function renderPermUser(user, index) {

    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td style={{width:"25%"}}>
          {user.username}
        </td>
        <td style={{width:"30%"}}>{user.surname}, {user.givenName}</td>
        <td style={{width:"5%"}}>
          <OverlayTrigger overlay={<Tooltip id="tooltip-remove-screen" style={{fontSize:'11px'}}>{t("I18N_TIP_Delete_perm")}</Tooltip>}>
            <span style={{cursor:'pointer', color:'SteelBlue'}}
                  onClick={() => handlePermUserDelete(user)}><BiTrash size={16} />
            </span>
          </OverlayTrigger>
        </td>
      </tr>
    )
  }


  // ----- Delete Confirmation Dialog - Key Permissions -----

  const [deleteTarget,            setDeleteTarget]            = useState();
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [errMsgDeleteConfirm,     setErrMsgDeleteConfirm]     = useState('');

  function handleObjDelete(obj) {
    setDeleteTarget(obj);
    setErrMsgDeleteConfirm('');
    setShowDeleteConfirmDialog(true);
  }

  function handleObjDeleteCancel() {
    setDeleteTarget(null);
    setErrMsgDeleteConfirm('');
    setShowDeleteConfirmDialog(false);
  }

  function handleObjDeleteSubmit() {

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_SECURITY_API.delete("key-permissions/" + deleteTarget.id);

        resolve(res.data);

        if (res.status === 200) {
          fetchKeyPerms(objAttrs.id);

          setErrMsg('');
          setShowDeleteConfirmDialog(false);
        }
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
        return;
      }
    });
  }

  // ----- END - Delete Confirmation Dialog - Key Permissions -----


  // ----- Add Perm User Stuff -----

  const handleAddPermUser = (e) => {
    setUserSelectorErrMsg('');
    setShowUserSelector(true);
  }

  function handleUserSelectorClose() {
    setUserSelectorErrMsg('');
    setShowUserSelector(false);
  }

  function handleAddPermUserSubmit(e, user) {

    const reqBody = {
            userList: [user.id]
          };

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_SECURITY_API.post("key-permissions/" + permAttrs.id + "/users", reqBody);

        resolve(res.data);

        if (res.status === 201) {
          setShowUserSelector(false);
          fetchKeyPermUsers(permAttrs.id);
        }
      } catch (error) {
        setUserSelectorErrMsg(t(translateAPIError(error)));
        return;
      }
    });
  }


  function renderUngrantedUser(user, index) {
    return (
      <tr key={index} style={{fontWeight:'300'}}>
        <td style={{maxWidth:'120px', flexWrap: 'wrap', wordWrap: 'break-word', backgroundColor:'#F0F0F0'}}>{user.username}</td>
        <td style={{maxWidth:'180px', flexWrap: 'wrap', wordWrap: 'break-word', backgroundColor:'#F0F0F0'}}>
          {(user.surname ? user.surname + ', ' : '')} {user.givenName} {user.middleName}</td>
        <td style={{maxWidth:'100px', minWidth: '100px', flexWrap: 'wrap', wordWrap: 'break-word', backgroundColor:'#F0F0F0'}}>
           { (i18n.language === 'en') ? user.deptName : (i18n.language === 'zh_HK' ? user.deptNameLang2 : user.deptNameLang3) }
        </td>
        <td style={{minWidth:'60px', flexWrap: 'wrap', wordWrap: 'break-word', backgroundColor:'#F0F0F0'}}>
          <span>
            <Button size="tiny" variant="outline-secondary"
              style={{ float: 'left' }}
              onClick={(e) => {handleAddPermUserSubmit(e, user)}}>
              {t("I18N_Add")}
            </Button>
          </span>
        </td>
      </tr>
    )
  }


  // ----- Delete Confirmation Dialog - Key Perm User -----

//const [deleteTarget,                setDeleteTarget]                = useState();
  const [showDeleteUserConfirmDialog, setShowDeleteUserConfirmDialog] = useState(false);
//const [errMsgDeleteConfirm,         setErrMsgDeleteConfirm]         = useState('');

  function handlePermUserDelete(obj) {
    setDeleteTarget(obj);
    setShowDeleteUserConfirmDialog(true);
  }

  function handlePermUserDeleteCancel() {
    setDeleteTarget(null);
    setErrMsgDeleteConfirm('');
    setShowDeleteUserConfirmDialog(false);
  }

  function handlePermUserDeleteSubmit() {

    const reqBody = {
            data: {
              userList: [deleteTarget.userId]
            }
          };

    return new Promise(async (resolve, reject) => {
      try {

        const res = await axiosAuth_SECURITY_API.delete("key-permissions/" + permAttrs.id  + "/users", reqBody);

        resolve(res.data);

        if (res.status === 200) {
          setShowDeleteUserConfirmDialog(false);
          fetchKeyPermUsers(permAttrs.id);
        }
      } catch (error) {
        setErrMsgDeleteConfirm(t(translateAPIError(error)));
        return;
      }
    });
  }

  // ----- END - Delete Confirmation Dialog - Key Perm User -----


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_Datakey_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_Datakey_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>

          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              <form>
                <Row className={classes.admin_pages__field_label}>
                  <Col>{t("I18N_Key_id")}:</Col>
                  <Col>{t("I18N_Description")}:</Col>
                  <Col>{t("I18N_Metadata")}:</Col>
                  <Col></Col>
                </Row>
                <Row className={classes.admin_pages__field_value}>
                  <Col md={3}>
                    {objAttrs.id}
                  </Col>
                  <Col md={3}>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%', minHeight:'100px', backgroundColor:'white'}}
                              name="description" value={objAttrs.description}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                  <Col md={6}>
                    <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%', minHeight:'100px', backgroundColor:'white'}}
                              name="metadata" value={objAttrs.metadata}
                      onChange={(e) => onAttrsInputChange(e)} />
                  </Col>
                </Row>

                <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                  <span>{t("I18N_Req_fields")}</span>
                </Row>

                <Row>
                  <div display='flex' style={{marginTop:'15px'}}>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ float: 'left' }}
                      onClick={handleEditCancel}>
                      {t("I18N_Cancel")}
                    </Button>
                    <Button size="tiny" variant="outline-secondary" className="mb-3"
                      style={{ marginLeft: '15px' }}
                      onClick={handleEditSubmit}>
                      {t("I18N_Save")}
                    </Button>
                    <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                      {errMsg}
                    </span>
                  </div>
                </Row>
              </form>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Key_id")}:</Col>
                <Col>{t("I18N_Description")}:</Col>
                <Col>{t("I18N_Key_owner")}:</Col>
                <Col>{t("I18N_Time_created")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{objAttrs.id}</Col>
                <Col style={{maxHeight:'120px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {objAttrs.description}
                </Col>
                <Col>{objAttrs.ownerUsername}</Col>
                <Col>{formatDate(objAttrs.timeCreated)}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col md={3}>{t("I18N_Key_value") + ' (' + t("I18N_Encrypted") + ')'}:</Col>
                <Col md={3}>{t("I18N_Key_IV")    + ' (' + t("I18N_Encrypted") + ')'}:</Col>
                <Col md={6}>{t("I18N_Metadata")}</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col md={3} style={{maxHeight:'120px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {objAttrs.keyValue}
                </Col>
                <Col md={3} style={{maxHeight:'120px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                  {objAttrs.ivValue}
                </Col>
                <Col md={6}>
                  <textarea readOnly rows={7} className={classes.admin_pages__field_editable} style={{minWidth: '100%' }}
                            onChange={() => {}} name="sitemap"
                            value={objAttrs.metadata} />
                </Col>
              </Row>
            </Container>
          }

        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'30px'}}>
          <div style={{display:'flex'}}>
            <span>
              {t("I18N_Key_permissions")}
            </span>
            <span style={{marginLeft:'auto', marginRight:'20px'}}>
              <OverlayTrigger overlay={<Tooltip id="tooltip-new_role" style={{fontSize:'11px'}}>{t("I18N_TIP_Add_perm_to_key")}</Tooltip>}>
                <span onClick={handleAddPerms} style={{ marginLeft: '0px', marginRight: '0px', marginTop: '3px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                  <BiPlus size={18} />
                </span>
              </OverlayTrigger>
            </span>
          </div>
        </div>

        <hr className="solid" style={{ margin: '0 0 0 0' }}></hr>

        <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px', maxHeight:'500px'}}>

          <Table hover responsive size="sm">
            <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
              <tr>
                <th style={{width:"25%", fontWeight:'400'}}>{t("I18N_Name")}</th>
                <th style={{width:"30%", fontWeight:'400'}}>{t("I18N_Description")}</th>
                <th style={{width:"40%", fontWeight:'400'}}>{t("I18N_Perm_setting")}</th>
                <th style={{width:"5%", fontWeight:'400'}}></th>
              </tr>
            </thead>
            <tbody>
              {permList.map(renderPerm)}
            </tbody>
          </Table>

        </div>

        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} >
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ marginLeft:'0px', fontSize:'12px', fontWeight:'300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
            </div>
          </Form.Group>
        </div>

        <Modal show={showAddPermsDialog} onHide={handleAddPermsClose} centered >
          <Modal.Body style={{height:'700px', overflow:'auto'}}>

            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <div display='flex'>
                <span>
                  {t("I18N_New_Key_Perm")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
            </div>

            <div style={{height:'590px', overflow:'auto', fontSize:'11px', marginTop:'10px'}}>
              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>
                  {t("I18N_Name")}: *
                </Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>
                  <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="name" value={permAttrs.name}
                         onChange={(e) => onAttrsInputChangePerm(e)} />
                </Col>
              </Row>
              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>
                  {t("I18N_Description")}: *
                </Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>
                  <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%', minHeight:'100px', backgroundColor:'white'}}
                            name="description" value={permAttrs.description} onChange={(e) => onAttrsInputChangePerm(e)} />
                </Col>
              </Row>
              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>
                  {t("I18N_Perm_setting")}:
                </Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>
                  <textarea row={7} className={classes.admin_pages__field_editable} style={{minWidth: '100%', minHeight:'100px', backgroundColor:'white'}}
                            name="permSetting" value={permAttrs.permSetting} onChange={(e) => onAttrsInputChangePerm(e)} />
                </Col>
              </Row>
            </div>

            <div display='flex' style={{ marginTop: '15px' }}>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ float: 'left' }}
                onClick={handleAddPermsClose}>
                {t("I18N_Cancel")}
              </Button>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ marginLeft: '15px' }}
                onClick={handleAddPermsSubmit}>
                {t("I18N_Submit")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {addPermErrMsg}
              </span>
            </div>
          </Modal.Body>
        </Modal>


        <Modal show={showPermDetailsDialog} onHide={handlePermDetailsDialogClose} centered >
          <Modal.Body style={{height:'700px', overflow:'auto'}}>
            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>

              <span>
                {t("I18N_Key_Permission")}
              </span>
              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

              <span>
                {t("I18N_Info")}
              </span>

              {editModePerm ?
                 null
               :
                 <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrsPerm}>
                   <PencilIcon size={12} />
                   {t("I18N_Edit")}
                 </span>
               }

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

              {editModePerm ?
                <Container>
                  <form>
                    <Row className={classes.admin_pages__field_label} >
                      <Col>
                        {t("I18N_Name")}: *
                      </Col>
                    </Row>
                    <Row className={classes.admin_pages__field_value}>
                      <Col>
                        <input className={classes.admin_pages__field_editable} style={{width:'100%'}} name="name" value={permAttrs.name}
                               onChange={(e) => onAttrsInputChangePerm(e)} />
                      </Col>
                    </Row>
                    <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                      <Col>
                        {t("I18N_Description")}: *
                      </Col>
                    </Row>
                    <Row className={classes.admin_pages__field_value}>
                      <Col>
                          <textarea className={classes.admin_pages__field_editable} style={{minWidth: '100%', minHeight:'50px'}}
                                    name="description" value={permAttrs.description}
                            onChange={(e) => onAttrsInputChange(e)} />
                      </Col>
                    </Row>
                    <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                      <Col>
                        {t("I18N_Perm_setting")}:
                      </Col>
                    </Row>
                    <Row className={classes.admin_pages__field_value}>
                      <Col>
                        <textarea row={7} className={classes.admin_pages__field_editable} style={{minWidth: '100%', minHeight:'150px'}}
                                  name="permSetting" value={permAttrs.permSetting} onChange={(e) => onAttrsInputChangePerm(e)} />
                      </Col>
                    </Row>

                    <div display='flex' style={{ marginTop: '15px' }}>
                      <Button size="tiny" variant="outline-secondary" className="mb-3"
                        style={{ float: 'left' }}
                        onClick={handleEditPermsCancel}>
                        {t("I18N_Cancel")}
                      </Button>
                      <Button size="tiny" variant="outline-secondary" className="mb-3"
                        style={{ marginLeft: '15px' }}
                        onClick={handleEditPermsSubmit}>
                        {t("I18N_Submit")}
                      </Button>
                      <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                        {addPermErrMsg}
                      </span>
                    </div>
                  </form>
                </Container>
                :
                <Container>
                  <Row className={classes.admin_pages__field_label} >
                    <Col>
                      {t("I18N_Name")}:
                    </Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col>
                      {permAttrs.name}
                    </Col>
                  </Row>
                  <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                    <Col>
                      {t("I18N_Description")}:
                    </Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col style={{maxHeight:'120px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                      {permAttrs.description}
                    </Col>
                  </Row>
                  <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                    <Col>
                      {t("I18N_Perm_setting")}:
                    </Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col style={{maxHeight:'120px', overflow:'auto', flexWrap:'wrap', wordWrap:'break-word'}}>
                      <textarea className={classes.admin_pages__field_editable} readOnly={true} style={{minWidth: '100%', minHeight:'100px'}}
                                name="permSetting" value={permAttrs.permSetting} onChange={(e) => onAttrsInputChangePerm(e)} />
                    </Col>
                  </Row>

                </Container>
              }

              <div style={{ marginTop: '15px', display:'flex' }}>
                <span>
                  {t("I18N_Users_granted_perm")}
                </span>
                <span style={{marginLeft:'auto', marginRight:'20px'}}>
                  <OverlayTrigger overlay={<Tooltip id="tooltip-new_role" style={{fontSize:'11px'}}>{t("I18N_TIP_Grant_new_user_to_perm")}</Tooltip>}>
                    <span onClick={handleAddPermUser} style={{marginRight: '0px', marginTop: '3px', cursor: 'pointer', color: 'rgb(100,100,100)' }}>
                      <BiPlus size={18} />
                    </span>
                  </OverlayTrigger>
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

              <div style={{overflow:'auto', fontSize:'11px', marginTop:'10px', height: '300px', maxHeight:'300px'}}>
                <Table hover responsive size="sm">
                  <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                    <tr>
                      <th style={{width:"30%", fontWeight:'400'}}>{t("I18N_Username")}</th>
                      <th style={{width:"65%", fontWeight:'400'}}>{t("I18N_Full_name")}</th>
                      <th style={{width:"5%", fontWeight:'400'}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {permUserList.map(renderPermUser)}
                  </tbody>
                </Table>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

              <div display='flex' style={{ marginTop: 'auto', marginBottom: '15px' }}>
                <Button size="tiny" variant="outline-secondary" className="mb-3"
                  style={{ float: 'left' }}
                  onClick={handlePermDetailsDialogClose}>
                  {t("I18N_Close")}
                </Button>
                <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                  {permDetailsErrMsg}
                </span>
              </div>

            </div>
          </Modal.Body>
        </Modal>


        <Modal show={showUserSelector} onHide={handleUserSelectorClose} centered >
          <Modal.Body style={{backgroundColor:'#F0F0F0'}}>

            <div className={classes.admin_pages__page_subtitle} style={{marginTop:'0px'}}>
              <div display='flex'>
                <span>
                  {t("I18N_Users")}
                </span>
              </div>

              <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>
            </div>

            <div style={{overflow:'auto', maxHeight:'500px', fontSize:'11px', marginTop:'10px'}}>
              <Table hover responsive size="sm">
                <thead style={{color:'#686868', fontSize:'12px', borderBottom:"2px solid #D0D0D0"}}>
                  <tr style={{maxWidth:'300px'}}>
                    <th style={{fontWeight:'400', backgroundColor:'#F0F0F0'}}>{t("I18N_Username")}</th>
                    <th style={{fontWeight:'400', backgroundColor:'#F0F0F0'}}>{t("I18N_Full_name")}</th>
                    <th style={{fontWeight:'400', backgroundColor:'#F0F0F0'}}>{t("I18N_Dept_name")}</th>
                    <th style={{fontWeight:'400', backgroundColor:'#F0F0F0'}}></th>
                  </tr>
                </thead>
                <tbody>
                  {ungrantedUserList ? ungrantedUserList.map(renderUngrantedUser) : null}
                </tbody>
              </Table>
            </div>

            <div style={{marginTop:'10px'}}>
              <Button size="tiny" variant="outline-secondary" className="mb-3"
                style={{ float: 'left' }}
                onClick={handleUserSelectorClose}>
                {t("I18N_Close")}
              </Button>
              <span className={classes.admin_pages__field_label} style={{ marginLeft: '30px', color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {userSelectorErrMsg}
              </span>
            </div>
          </Modal.Body>
        </Modal>


        <DeleteConfirmDialog
          title={t("I18N_Key_perm_delete_confirm")}
          show={showDeleteConfirmDialog}
          targetName={deleteTarget ? deleteTarget.name : ''}
          errMsgDeleteConfirm={errMsgDeleteConfirm}
          onCancel={handleObjDeleteCancel}
          onSubmit={handleObjDeleteSubmit}
        />


        <DeleteConfirmDialog
          title={t("I18N_Key_perm_user_delete_confirm")}
          show={showDeleteUserConfirmDialog}
          targetName={deleteTarget ? deleteTarget.username : ''}
          errMsgDeleteConfirm={errMsgDeleteConfirm}
          onCancel={handlePermUserDeleteCancel}
          onSubmit={handlePermUserDeleteSubmit}
        />


      </Card>
    </>
  );
}

export default DatakeyAdminDetailsView;
